export interface IrisReportResponse {
  civilId: string;
  irisUsers: IrisUser[];
}

export class IrisSummaryFiltersDTO {
  dateFrom?: string;
  dateTo?: string;
  description?: string;
  amountFrom?: number;
  amountTo?: number;
}

export interface IrisUser {
  id: string;
  brand: string;
  email: string;
  name: string;
  bankAccounts: IrisBankAccount[];
}

export interface IrisBankAccount {
  id: number;
  name: string;
  iban: string;
  product: string;
  ownerName: string;
  currency: string;
  bankName: string;
  country: string;
  consents: IrisConsent[];
  balances: IrisBalance[];
  transactions: IrisTransaction[];
}

export interface IrisConsent {
  ibanId: number;
  valid_until: string;
  frequency_per_day: number;
  created_at: string;
  status: IrisConsentStatus;
  permissions?: { name: string }[];
}

export interface IrisBalance {
  amount: number;
  currency: string;
  balanceType: IrisBalanceType;
  referenceDate: string | null;
  creditLimitIncluded: boolean | null;
}

export interface IrisTransaction {
  transactionId: string;
  bookingDate: string;
  creditorAccount: { iban: string } | null;
  creditorName: string | null;
  debtorAccount: { iban: string } | null;
  debtorName: string | null;
  entryReference: string | null;
  remittanceInformationUnstructured: string | null;
  transactionAmount: IrisTransactionAmount;
  exchangeRate: IrisExchangeRate | null;
  valueDate: string;
  creditDebitIndicator: IrisCreditDebitIndicator;
  category?: IrisTransactionCategory;
}

export interface IrisTransactionAmount {
  amount: number;
  currency: string;
}

export interface IrisExchangeRate {
  currencyFrom: string;
  rateFrom: string;
  currencyTo: string;
  rateTo: string;
  rateDate: string;
}

export interface IrisTransactionCategory {
  code: string;
  name: string;
}

export interface IrisReportSummary {
  civilId: string;
  totalAccounts: number;
  totalBalance: number;
  hasValidConsent: boolean;
  brands: IrisBrandSummary[];
}

export interface IrisBrandSummary {
  brand: string;
  name: string;
  accounts: IrisAccountSummary[];
}

export interface IrisAccountSummary {
  iban: string;
  bankName: string;
  currency: string;
  ownerName: string;
  balance: number | null;
  consentStatus: string;
  consentValidUntil: string | null;
  transactionCount: number;
}


export interface IrisUserActivity {
  id: number;
  brand: string;
  fullName: string;
  civilId: string;
  email: string;
  lastRequestAt: string | null;
  lastConsentAt: string | null;
  requestsCountForPeriod: number;
  consentsCountForPeriod: number;
}

export enum UserActivitySortField {
  LastRequestAt = 'lastRequestAt',
  LastConsentAt = 'lastConsentAt',
  RequestsForPeriod = "requestsForPeriod",
  ConsentsForPeriod = "consentsForPeriod",
}

export enum SortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export enum ActivityPeriod {
  OneDay = '1d',
  SevenDays = '7d',
  ThirtyDays = '30d',
}

export interface IrisActivityFiltersDTO {
  civilId?: string;
  name?: string;
  email?: string;
  brand?: string;
  sortBy?: UserActivitySortField;
  direction?: SortDirection;
  limit?: number;
  offset?: number;
  period: ActivityPeriod
}

export interface IrisUserActivityDetail {
  consents: IrisConsent[];
  timeline: IrisTimelineEvent[];
}

export interface IrisConsent {
  created_at: string,
  deactivated_at: string,
  deactivation_reason: string | null,
  iban: string,
  bank_name: string,
  owner_name: string,
  valid_until: string,
}

export enum IrisTimelineEventType {
  CONSENT_REQUEST = 'consent_request',
  CONSENT_CREATED = 'consent_created',
  CONSENT_DEACTIVATED = 'consent_deactivated',
}

export enum IrisDeactivationReason {
  OPERATOR = 'OPERATOR',
  NOT_FOUND = 'NOT_FOUND',
  CONSENT_EXPIRED = 'CONSENT_EXPIRED',
  MORE_THAN_ONE_CONSENT = 'MORE_THAN_ONE_CONSENT',
}

export interface IrisTimelineEvent {
  type: IrisTimelineEventType;
  bankName: string;
  product: string | null;
  createdAt: string;
  errorMessage: string | null;
  deactivatedAt: string | null;
  deactivatedBy: string | null;
  deactivationReason: IrisDeactivationReason | null;
  iban: string | null;
  accountName: string | null;
  ownerName: string | null;
}

export interface IrisTimelineGroup {
  date: string;
  events: IrisTimelineEvent[];
}

export enum IrisCreditDebitIndicator {
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
}

export enum IrisConsentStatus {
  VALID = 'valid',
  INVALID = 'invalid'
}

export enum IrisBalanceType {
  INTERIM_AVAILABLE = 'interimAvailable',
  OPENING_BOOKED = 'openingBooked',
  EXPECTED = 'expected',
  AUTHORISED = 'authorised',
  FORWARD_AVAILABLE = 'forwardAvailable',
  INFORMATION = 'information',
  CLOSING_BOOKED = 'closingBooked',
  INTERIM_BOOKED = 'interimBooked',
}

export interface TransactionDirectionStyle {
  icon: string;
  color: string;
  label: string;
}

export const TRANSACTION_DIRECTION_STYLE_MAP: { [key in IrisCreditDebitIndicator]: TransactionDirectionStyle } = {
  [IrisCreditDebitIndicator.CREDIT]: {
    icon: 'call_received',
    color: '#23a800',
    label: 'bankInfo.incoming',
  },
  [IrisCreditDebitIndicator.DEBIT]: {
    icon: 'call_made',
    color: '#c9211e',
    label: 'bankInfo.outgoing',
  },
};

export interface TransactionRow extends IrisTransaction {
  counterpartyName: string;
  counterpartyIban: string;
}